import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PaymentDialog } from "@/components/dialogs/PaymentDialog";
import api from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { TrendingUp } from "lucide-react";

const ageLabel = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Oggi";
  return `Da ${days} ${days === 1 ? "giorno" : "giorni"}`;
};

export const ReceivablesCard = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(false);
  const [pay, setPay] = useState(null); // {owner_id, dog_id, session_id?, amount, package?}

  const load = useCallback(() => {
    api.get("/dashboard/receivables").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const items = data.items || [];
  const empty = data.position_count === 0;

  const goClient = (oid) => { if (oid) { setDetail(false); onNavigate(`/clienti/${oid}`); } };
  const goSource = (it) => { if (it.dog_id) { setDetail(false); onNavigate(`/cani/${it.dog_id}?focus=${it.source_type}:${it.source_id}`); } };

  const startPay = (it) => {
    if (it.source_type === "package") {
      setPay({ owner_id: it.owner_id, dog_id: it.dog_id, package: { id: it.source_id, outstanding: it.residual_amount } });
    } else {
      setPay({ owner_id: it.owner_id, dog_id: it.dog_id, session_id: it.source_id, amount: it.residual_amount });
    }
  };

  return (
    <>
      <button type="button" data-testid="receivables-card" onClick={() => !empty && setDetail(true)}
        className={`w-full text-left rounded-2xl border border-border bg-card p-4 ${empty ? "cursor-default" : "hover:border-accent transition-colors"}`}>
        <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-wide">
          <TrendingUp className="h-4 w-4 text-accent" /> Da incassare
        </div>
        {empty ? (
          <div className="mt-2 text-sm text-muted-foreground" data-testid="receivables-empty">Nessun importo in sospeso</div>
        ) : (
          <div className="mt-1">
            <div className="font-head font-extrabold text-2xl tabular" data-testid="receivables-total">{fmtEUR(data.total_outstanding)}</div>
            <div className="text-sm text-muted-foreground">
              {data.position_count} {data.position_count === 1 ? "posizione" : "posizioni"} · {data.client_count} {data.client_count === 1 ? "cliente" : "clienti"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">Prestazioni {fmtEUR(data.sessions_total)} · Pacchetti {fmtEUR(data.packages_total)}</div>
            <div className="text-sm font-semibold text-accent mt-2">Vedi dettaglio →</div>
          </div>
        )}
      </button>

      <Dialog open={detail} onOpenChange={setDetail}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">Da incassare</DialogTitle>
            <DialogDescription>Importi ancora da incassare, dal più vecchio.</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-muted-foreground -mt-1">Totale <b className="text-foreground">{fmtEUR(data.total_outstanding)}</b></div>
          <div className="space-y-2 mt-2" data-testid="receivables-list">
            {items.map((it) => (
              <div key={`${it.source_type}-${it.source_id}`} className="rounded-xl border border-border p-3" data-testid={`receivable-${it.source_id}`}>
                <button type="button" onClick={() => goClient(it.owner_id)} className="font-semibold hover:underline text-left" data-testid={`receivable-owner-${it.source_id}`}>{it.owner_name || "—"}</button>
                <button type="button" onClick={() => goSource(it)} className="block text-sm hover:underline text-left" data-testid={`receivable-source-${it.source_id}`}>
                  {it.dog_name ? `${it.dog_name} · ` : ""}{it.label} · {fmtDate(it.date)}
                </button>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <div>
                    <span className="text-xs text-muted-foreground">{it.source_type === "package" ? "Da saldare" : "Da incassare"}</span>
                    <span className="ml-2 font-semibold tabular text-[#8D6E63]">{fmtEUR(it.residual_amount)}</span>
                    {ageLabel(it.date) && <span className="ml-2 text-xs text-muted-foreground">· {ageLabel(it.date)}</span>}
                  </div>
                  <Button size="sm" className="rounded-full shrink-0" data-testid={`receivable-pay-${it.source_id}`} onClick={() => startPay(it)}>Registra pagamento</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <PaymentDialog
        open={!!pay}
        onOpenChange={(v) => !v && setPay(null)}
        presetOwnerId={pay?.owner_id}
        presetDogId={pay?.dog_id}
        presetSessionId={pay?.session_id}
        presetAmount={pay?.amount}
        presetPackage={pay?.package}
        onSaved={() => { setPay(null); load(); }}
      />
    </>
  );
};
